import React, { createContext, useContext, useState, useCallback } from 'react';

interface BrainstormingIdeaFormData {
  name: string;
  affecting_metric: string;
  description: string;
  submitted_by: string;
  sales_channel_id: string;
}

interface EditingIdea {
  id: string;
  name: string;
  affecting_metric: string[] | string;
  description: string;
  submitted_by: string;
  sales_channel_id: string;
}

interface BrainstormingIdeaDialogContextType {
  isOpen: boolean;
  isEditing: boolean;
  editingIdea: EditingIdea | null;
  formData: BrainstormingIdeaFormData;
  setFormData: React.Dispatch<React.SetStateAction<BrainstormingIdeaFormData>>;
  openCreateDialog: (salesChannelId: string, onSuccess?: () => void) => void;
  openEditDialog: (idea: EditingIdea, onSuccess?: () => void) => void;
  closeDialog: () => void;
  onSuccessCallback: (() => void) | null;
}

const BrainstormingIdeaDialogContext = createContext<BrainstormingIdeaDialogContextType | undefined>(undefined);

export const useBrainstormingIdeaDialog = () => {
  const context = useContext(BrainstormingIdeaDialogContext);
  if (!context) {
    throw new Error('useBrainstormingIdeaDialog must be used within a BrainstormingIdeaDialogProvider');
  }
  return context;
};

const initialFormData: BrainstormingIdeaFormData = {
  name: "",
  affecting_metric: "",
  description: "",
  submitted_by: "",
  sales_channel_id: ""
};

export const BrainstormingIdeaDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingIdea, setEditingIdea] = useState<EditingIdea | null>(null);
  const [formData, setFormData] = useState<BrainstormingIdeaFormData>(initialFormData);
  const [onSuccessCallback, setOnSuccessCallback] = useState<(() => void) | null>(null);

  const openCreateDialog = useCallback((salesChannelId: string, onSuccess?: () => void) => {
    setFormData({
      ...initialFormData,
      sales_channel_id: salesChannelId
    });
    setEditingIdea(null);
    setIsEditing(false);
    setOnSuccessCallback(() => onSuccess || null);
    setIsOpen(true);
  }, []);

  const openEditDialog = useCallback((idea: EditingIdea, onSuccess?: () => void) => {
    setFormData({
      name: idea.name,
      affecting_metric: Array.isArray(idea.affecting_metric) ? idea.affecting_metric[0] || "" : idea.affecting_metric || "",
      description: idea.description,
      submitted_by: idea.submitted_by,
      sales_channel_id: idea.sales_channel_id
    });
    setEditingIdea(idea);
    setIsEditing(true);
    setOnSuccessCallback(() => onSuccess || null);
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setIsEditing(false);
    setEditingIdea(null);
    setFormData(initialFormData);
    setOnSuccessCallback(null);
  }, []);

  return (
    <BrainstormingIdeaDialogContext.Provider
      value={{
        isOpen,
        isEditing,
        editingIdea,
        formData,
        setFormData,
        openCreateDialog,
        openEditDialog,
        closeDialog,
        onSuccessCallback
      }}
    >
      {children}
    </BrainstormingIdeaDialogContext.Provider>
  );
};
